/**
 * Core API Client
 *
 * Base API client with authentication, request deduplication, and error handling.
 * Domain-specific clients extend this base client.
 *
 * Refactored for improved readability and maintainability.
 */

import { TokenStorage } from "./storage";
import { emitAuthEvent } from "./events";
import type { ApiError, ApiResponse, RequestOptions } from "./types";
import { getEffectiveOrgId } from "./tenant-state";

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_TIMEOUT = 30000; // 30 seconds
const AUTH_REFRESH_ENDPOINT = "/identity/auth/sessions/refresh";
const AUTH_LOGIN_ENDPOINT = "/identity/auth/sessions";
const AUTH_REGISTER_ENDPOINT = "/identity/auth/signup";

// ============================================================================
// Configuration Helpers
// ============================================================================

/**
 * Get current organization ID from tenant state module
 */
function getCurrentOrgId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return getEffectiveOrgId();
}

/**
 * Get API base URL from environment variables
 * Priority: Runtime config (window.env) > Build-time config (VITE_API_BASE) > Default
 */
export const getApiBase = (): string => {
  // 1. Runtime config from /env.js (highest priority - injected at container startup)
  let resolved = "";
  if (typeof window !== "undefined" && (window as any).env?.API_BASE_URL) {
    const runtimeUrl = (window as any).env.API_BASE_URL;
    if (
      runtimeUrl &&
      typeof runtimeUrl === "string" &&
      runtimeUrl !== "undefined" &&
      runtimeUrl !== "None" &&
      runtimeUrl.trim() !== "" &&
      runtimeUrl.startsWith("http")
    ) {
      resolved = runtimeUrl.trim();
    }
  }

  // 2. Build-time config (fallback - baked into bundle via Vite)
  if (!resolved) {
    let envApiBase: string | undefined;

    try {
      const viteEnv = (import.meta as any)?.env;
      if (viteEnv?.VITE_API_BASE && typeof viteEnv.VITE_API_BASE === "string") {
        envApiBase = viteEnv.VITE_API_BASE;
      }
    } catch {
      // import.meta not available (e.g., Node.js environment)
    }

    // Fallback to Next.js env var
    if (
      !envApiBase &&
      typeof process !== "undefined" &&
      process.env?.NEXT_PUBLIC_API_BASE
    ) {
      const nextValue = process.env.NEXT_PUBLIC_API_BASE;
      if (typeof nextValue === "string") {
        envApiBase = nextValue;
      }
    }

    // Validate and return env var if set
    if (
      envApiBase &&
      envApiBase !== "undefined" &&
      envApiBase !== "None" &&
      envApiBase.trim() !== "" &&
      envApiBase.startsWith("http")
    ) {
      resolved = envApiBase.trim();
    }
  }

  // 3. Default fallback: Use Fastify API server for local development
  if (!resolved) {
    resolved = "http://localhost:4000";
  }

  // Edge/Safari: keep API host aligned with the page host (localhost vs 127.0.0.1).
  // Mismatched loopback hosts are different origins and can break auth/account loads.
  if (typeof window !== "undefined") {
    try {
      const apiUrl = new URL(resolved);
      const pageHost = window.location.hostname;
      const loopback = new Set(["localhost", "127.0.0.1"]);
      if (
        loopback.has(apiUrl.hostname) &&
        loopback.has(pageHost) &&
        apiUrl.hostname !== pageHost
      ) {
        apiUrl.hostname = pageHost;
        resolved = apiUrl.origin;
      }
    } catch {
      /* ignore */
    }
  }

  return resolved;
};

/**
 * Get API base URL with debug logging in development
 */
const getApiBaseUrl = (): string => {
  const base = getApiBase();
  if (typeof window !== "undefined") {
    try {
      const viteEnv = (import.meta as any)?.env;
      if (viteEnv?.DEV) {
      }
    } catch {
      // Ignore if import.meta is not available
    }
  }
  return base;
};

// ============================================================================
// URL Building Helpers
// ============================================================================

/**
 * Normalize endpoint path (remove leading slash)
 */
function normalizeEndpoint(endpoint: string): string {
  return endpoint.startsWith("/") ? endpoint.substring(1) : endpoint;
}

/**
 * Build query string from params object
 */
function buildQueryString(
  params?: Record<string, string | number | boolean | undefined | null>,
): string {
  if (!params) return "";

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

/**
 * Build full URL from base URL, endpoint, and query params
 */
function buildUrl(
  baseUrl: string,
  endpoint: string,
  queryString?: string,
): string {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const url = queryString
    ? `${baseUrl}/${normalizedEndpoint}?${queryString}`
    : `${baseUrl}/${normalizedEndpoint}`;
  return url;
}

// ============================================================================
// Header Building Helpers
// ============================================================================

/**
 * Build request headers with authentication and content type
 */
function buildHeaders(
  options: RequestOptions,
  token: string | null,
  orgId: string | null,
  endpoint: string,
): HeadersInit {
  const hasBody = options.body !== undefined && options.body !== null;
  const normalizedEndpoint = normalizeEndpoint(endpoint);

  // Start with user-provided headers (but exclude Authorization to ensure we use fresh token)
  const userHeaders = { ...options.headers };
  delete (userHeaders as Record<string, any>)["Authorization"];
  delete (userHeaders as Record<string, any>)["authorization"];

  // For FormData, browser must set Content-Type with boundary - don't allow override
  const isFormData = hasBody && options.body instanceof FormData;
  if (isFormData) {
    delete (userHeaders as Record<string, any>)["Content-Type"];
    delete (userHeaders as Record<string, any>)["content-type"];
  }

  const headers: HeadersInit = {
    // Only set Content-Type for JSON bodies (string bodies are assumed to be JSON)
    // For FormData, the browser will automatically set Content-Type with boundary
    // For other body types, services should handle headers appropriately
    ...(hasBody &&
      typeof options.body === "string" &&
      !isFormData && { "Content-Type": "application/json" }),
    ...userHeaders,
  };

  // Add JWT token if available (skip for refresh endpoint)
  // This MUST come after user headers to ensure fresh token is used
  if (token && !normalizedEndpoint.includes(AUTH_REFRESH_ENDPOINT)) {
    const authHeader = `Bearer ${token}`;
    (headers as Record<string, string>)["Authorization"] = authHeader;

    // Debug logging for retries to verify token is being set
    if (typeof window !== "undefined" && (import.meta as any)?.env?.DEV) {
      // Only log in dev mode and for retries (check via endpoint pattern or caller context)
      // This helps diagnose 401 retry issues
    }
  } else if (!normalizedEndpoint.includes(AUTH_REFRESH_ENDPOINT)) {
  }

  // Add X-Org-Id header ONLY if explicitly provided via options.orgId (admin impersonation)
  // Default behavior: use JWT orgId from token (canonical) - don't send header
  const orgIdOverride = options.orgId;
  if (orgIdOverride) {
    (headers as Record<string, string>)["X-Org-Id"] = orgIdOverride;
  }

  return headers;
}

// ============================================================================
// Abort Signal Management
// ============================================================================

interface AbortSignalConfig {
  signal: AbortSignal;
  cleanup: () => void;
}

/**
 * Create combined abort signal from timeout and React Query signal
 */
function createAbortSignal(
  timeout: number,
  existingSignal?: AbortSignal,
): AbortSignalConfig {
  const controller = new AbortController();
  let timeoutId: NodeJS.Timeout | null = null;
  let abortListener: (() => void) | null = null;

  const cleanup = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (abortListener && existingSignal) {
      existingSignal.removeEventListener("abort", abortListener);
      abortListener = null;
    }
  };

  // Set up timeout - pass reason to avoid "aborted without reason" in console
  timeoutId = setTimeout(() => {
    if (!controller.signal.aborted) {
      controller.abort(new DOMException("Request timeout", "TimeoutError"));
    }
  }, timeout);

  // Handle existing signal (from React Query)
  if (existingSignal) {
    // Check if already aborted
    if (existingSignal.aborted) {
      cleanup();
      throw new DOMException("The operation was aborted.", "AbortError");
    }

    // Use AbortSignal.any() if available (modern browsers)
    if (typeof AbortSignal.any === "function") {
      return {
        signal: AbortSignal.any([existingSignal, controller.signal]),
        cleanup,
      };
    }

    // Fallback: listen to existing signal and abort controller when it aborts
    abortListener = () => {
      cleanup();
      if (!controller.signal.aborted) {
        controller.abort();
      }
    };
    existingSignal.addEventListener("abort", abortListener);

    return {
      signal: controller.signal,
      cleanup,
    };
  }

  return {
    signal: controller.signal,
    cleanup,
  };
}

// ============================================================================
// Error Handling Helpers
// ============================================================================

/**
 * Check if error is an abort error (from React Query or timeout)
 */
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException || (error as any)?.name === "AbortError";
}

/**
 * Check if error is a timeout error
 */
function isTimeoutError(error: unknown): boolean {
  return (
    (error as any)?.name === "TimeoutError" ||
    String((error as any)?.message || "").includes("timeout") ||
    String((error as any)?.message || "").includes("timed out")
  );
}

/**
 * Detect and categorize network errors
 */
function categorizeNetworkError(
  error: unknown,
  url: string,
  timeout: number,
): { type: string; message: string } {
  const errorMessage = (error as any)?.message || `Failed to connect to ${url}`;

  // CORS errors
  if (
    (error as any)?.name === "TypeError" &&
    (errorMessage.includes("Failed to fetch") ||
      errorMessage.includes("NetworkError") ||
      errorMessage.includes("Load failed"))
  ) {
    const isLikelyCORS =
      !(error as any)?.response &&
      url.startsWith("https://") &&
      typeof window !== "undefined";

    if (isLikelyCORS) {
      return {
        type: "CORS Error",
        message: `CORS policy blocked the request to ${url}. The API Gateway needs to allow requests from ${window.location.origin}. Please contact support or check API Gateway CORS configuration.`,
      };
    }

    return {
      type: "Network Error",
      message:
        "Network error. Please check your internet connection and try again.",
    };
  }

  // Timeout errors
  if (isTimeoutError(error)) {
    return {
      type: "Timeout Error",
      message: `Request timed out after ${timeout}ms. The server may be slow or unavailable.`,
    };
  }

  // Generic network error
  return {
    type: "Network Error",
    message: errorMessage,
  };
}

/**
 * Parse error response from API
 */
async function parseErrorResponse(response: Response): Promise<ApiError> {
  let errorData: any = {};
  let errorText = "";

  try {
    errorText = await response.text();

    if (errorText && errorText.trim().length > 0) {
      try {
        errorData = JSON.parse(errorText);
      } catch {
        // If JSON parsing fails, use raw text
        errorData = {
          error: errorText.substring(0, 100),
          message: errorText,
        };
      }
    } else {
      // Empty response
      errorData = {
        error: `HTTP ${response.status}: ${response.statusText}`,
        message: `Request failed with status ${response.status}. The server returned an empty response.`,
      };
    }
  } catch (e: any) {
    // If reading response fails entirely
    errorData = {
      error: "Failed to read error response",
      message: `HTTP ${response.status}: ${
        response.statusText
      }. Could not read error details: ${e?.message || "Unknown error"}`,
    };
  }

  const apiGatewayMessage =
    errorData.message || errorData.detail || errorData.error;
  let userMessage =
    apiGatewayMessage || `HTTP ${response.status}: ${response.statusText}`;
  let code: string | undefined = errorData.code;
  let retryAfterSeconds: number | undefined;

  const retryAfterHeader =
    response.headers.get("retry-after") ||
    response.headers.get("Retry-After");
  if (retryAfterHeader) {
    const asInt = Number.parseInt(retryAfterHeader, 10);
    if (Number.isFinite(asInt) && asInt >= 0) {
      retryAfterSeconds = asInt;
    } else {
      const asDate = Date.parse(retryAfterHeader);
      if (!Number.isNaN(asDate)) {
        retryAfterSeconds = Math.max(
          0,
          Math.ceil((asDate - Date.now()) / 1000),
        );
      }
    }
  }

  // Provide user-friendly messages for common errors
  if (response.status === 401) {
    userMessage =
      apiGatewayMessage ||
      "Invalid email or password. Please check your credentials and try again.";
  } else if (response.status === 403) {
    if (apiGatewayMessage?.includes("Missing Authentication Token")) {
      userMessage = `The endpoint may not be configured on the API Gateway, or the route path is incorrect. Please verify the backend API routes are deployed.`;
    } else if (
      apiGatewayMessage?.includes("Invalid key=value pair") ||
      apiGatewayMessage?.includes("Authorization header") ||
      apiGatewayMessage?.includes("hashed with SHA-256")
    ) {
      userMessage = `The endpoint appears to be misconfigured on the API Gateway. It may require a different authentication method or the endpoint may not exist.`;
    } else {
      userMessage =
        "Access forbidden. You don't have permission to access this resource.";
    }
  } else if (response.status === 404) {
    userMessage = "Resource not found. The requested endpoint does not exist.";
  } else if (response.status === 429) {
    code = code || "RATE_LIMIT_EXCEEDED";
    userMessage =
      retryAfterSeconds != null && retryAfterSeconds > 0
        ? `Too many requests — try again in ${retryAfterSeconds} seconds.`
        : apiGatewayMessage ||
          "Too many requests — please wait and try again.";
  } else if (response.status === 500) {
    userMessage = apiGatewayMessage || "Server error. Please try again later.";
  } else if (response.status === 502 || response.status === 503) {
    userMessage = "Service temporarily unavailable. Please try again later.";
  }

  return {
    error: errorData.error || errorData.message || `HTTP ${response.status}`,
    message: userMessage,
    statusCode: response.status,
    code,
    ...(retryAfterSeconds != null ? { retryAfterSeconds } : {}),
  };
}

/**
 * Parse 401 error response
 */
async function parse401Error(response: Response): Promise<string> {
  try {
    const errorText = await response.text();
    if (errorText && errorText.trim().length > 0) {
      try {
        const errorData = JSON.parse(errorText);
        return errorData.message || errorData.error || "Unauthorized";
      } catch {
        return errorText;
      }
    }
  } catch {
    // If reading fails, use default
  }
  return "Unauthorized";
}

// ============================================================================
// Response Parsing Helpers
// ============================================================================

/**
 * Parse JSON response from API
 */
async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  if (!text || text.trim().length === 0) {
    // Some endpoints return 204 No Content, which is valid
    if (response.status === 204) {
      return {} as T;
    }
    throw new Error("Empty response body");
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(
      `Server returned invalid JSON. Please try again or contact support if the problem persists.`,
    );
  }
}

/**
 * Validate API response shape
 */
function validateResponse<T>(parsed: unknown, status?: number): ApiResponse<T> {
  // 204 No Content: server may send empty body or body like "null" – always treat as success
  if (status === 204) {
    return { data: null as any } as ApiResponse<T>;
  }

  if (!parsed || typeof parsed !== "object") {
    throw new Error("Invalid API response: not an object");
  }

  if (!("data" in parsed)) {
    throw new Error("Invalid API response: missing 'data' field");
  }

  return parsed as ApiResponse<T>;
}

// ============================================================================
// Main API Client Class
// ============================================================================

export class ApiClient {
  protected baseUrl: string;
  protected token: string | null = null;
  protected refreshTokenValue: string | null = null;

  // Refresh queue: prevents multiple simultaneous refresh attempts
  private refreshPromise: Promise<void> | null = null;

  // Track last refresh attempt time to prevent rate limiting
  private lastRefreshAttempt: number = 0;
  private refreshRetryDelay: number = 0; // Exponential backoff delay in ms

  // Request deduplication: store pending requests by cache key
  // Only used for GET requests (mutating requests should not be deduped)
  private pendingRequests = new Map<string, Promise<ApiResponse<any>>>();

  constructor(baseUrl?: string) {
    const resolvedBaseUrl = baseUrl || getApiBaseUrl();
    this.baseUrl = resolvedBaseUrl.endsWith("/")
      ? resolvedBaseUrl.slice(0, -1)
      : resolvedBaseUrl;

    // Load tokens from storage on initialization
    if (typeof window !== "undefined") {
      this.token = TokenStorage.get("auth_token");
      this.refreshTokenValue = TokenStorage.get("refresh_token");
    }
  }

  // ==========================================================================
  // Token Management
  // ==========================================================================

  /**
   * Set authentication token (encrypted) with TTL
   * @param token - The access token to store
   * @param ttlSeconds - Optional TTL in seconds. If not provided, defaults to 15 minutes (900 seconds)
   */
  setToken(token: string | null, ttlSeconds?: number): void {
    const defaultTTL = 15 * 60; // 15 minutes
    const finalTTL = token ? (ttlSeconds ?? defaultTTL) : undefined;
    this.token = token;
    // Use provided TTL or default to 15 minutes (matching backend JWT expiry)
    TokenStorage.set("auth_token", token, finalTTL);
  }

  /**
   * Set refresh token (encrypted localStorage for MVP) with TTL
   * SECURITY: In production, backend should set httpOnly cookie instead
   * @param refreshToken - The refresh token to store
   * @param ttlSeconds - Optional TTL in seconds. If not provided, defaults to 7 days
   */
  setRefreshToken(refreshToken: string | null, ttlSeconds?: number): void {
    const defaultTTL = 7 * 24 * 60 * 60; // 7 days
    const finalTTL = refreshToken ? (ttlSeconds ?? defaultTTL) : undefined;
    this.refreshTokenValue = refreshToken;
    // Use provided TTL or default to 7 days (matching backend JWT expiry)
    TokenStorage.set("refresh_token", refreshToken, finalTTL);
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Get current refresh token
   * SECURITY: In production, this should read from httpOnly cookie (backend sets it)
   */
  getRefreshToken(): string | null {
    return this.refreshTokenValue;
  }

  /**
   * Check if user is authenticated
   * Always checks storage to ensure sync between instances
   */
  isAuthenticated(): boolean {
    if (typeof window !== "undefined") {
      const storageToken = TokenStorage.get("auth_token");
      if (storageToken && storageToken !== this.token) {
        this.token = storageToken;
      }
      return !!storageToken;
    }
    return !!this.token;
  }

  // ==========================================================================
  // Request Deduplication
  // ==========================================================================

  /**
   * Generate cache key for request deduplication
   */
  private getCacheKey(endpoint: string, options: RequestOptions = {}): string {
    const method = options.method || "GET";

    // Handle body deterministically
    let bodyKey = "";
    if (options.body) {
      if (typeof options.body === "string") {
        try {
          const parsed = JSON.parse(options.body);
          bodyKey = JSON.stringify(parsed);
        } catch {
          bodyKey = options.body;
        }
      } else {
        bodyKey = JSON.stringify(options.body);
      }
    }

    // Handle query parameters deterministically
    let paramsKey = "";
    if (options.params) {
      const sortedParams = Object.keys(options.params)
        .sort()
        .reduce(
          (acc, key) => {
            const value = options.params![key];
            if (value !== undefined && value !== null) {
              acc[key] = String(value);
            }
            return acc;
          },
          {} as Record<string, string>,
        );
      paramsKey = JSON.stringify(sortedParams);
    }

    return `${method}:${endpoint}:${bodyKey}:${paramsKey}`;
  }

  // ==========================================================================
  // Token Refresh
  // ==========================================================================

  /**
   * Abstract method for token refresh - must be implemented by auth client
   */
  protected async refreshToken(): Promise<void> {
    throw new Error("refreshToken must be implemented by auth client");
  }

  /**
   * Safe token refresh with queue
   * Prevents race conditions with refresh queue
   * Implements exponential backoff for rate-limited requests
   */
  protected async refreshTokenSafely(): Promise<void> {
    // If refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check if we need to wait due to rate limiting
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastRefreshAttempt;

    if (
      this.refreshRetryDelay > 0 &&
      timeSinceLastAttempt < this.refreshRetryDelay
    ) {
      const waitTime = this.refreshRetryDelay - timeSinceLastAttempt;

      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }

    // Create new refresh promise
    this.refreshPromise = (async () => {
      try {
        this.lastRefreshAttempt = Date.now();

        // Check if this instance has refreshToken implemented (AuthApiClient)
        const hasRefreshToken =
          typeof (this as any).refreshToken === "function";
        const isAbstractMethod = (this as any).refreshToken
          ?.toString()
          .includes("throw new Error");

        if (hasRefreshToken && !isAbstractMethod) {
          // This is AuthApiClient, use its refreshToken method
          await (this as any).refreshToken();
        } else {
          // This is regular ApiClient, delegate to authApiClient singleton
          const { authApiClient } =
            await import("@/services/domains/identity/auth.service");
          await authApiClient.refreshToken();
          // Sync token after refresh
          const newToken =
            typeof window !== "undefined"
              ? TokenStorage.get("auth_token")
              : null;
          if (newToken) {
            this.token = newToken;
          }
        }

        // Reset retry delay on success
        this.refreshRetryDelay = 0;
      } catch (error: any) {
        // Handle rate limiting (429) with exponential backoff
        if (error.status === 429 || error.statusCode === 429) {
          // Prefer parsed Retry-After from ApiError; otherwise exponential backoff
          const retryAfter =
            typeof error.retryAfterSeconds === "number"
              ? error.retryAfterSeconds
              : undefined;
          if (retryAfter != null && retryAfter > 0) {
            this.refreshRetryDelay = retryAfter * 1000;
          } else {
            // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
            this.refreshRetryDelay = Math.min(
              (this.refreshRetryDelay || 1000) * 2,
              30000,
            );
          }
        }
        throw error;
      } finally {
        // Clear promise when done
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  // ==========================================================================
  // Request Execution
  // ==========================================================================

  /**
   * Make authenticated API request
   * Only dedupe GET requests (mutating requests should not be deduped)
   */
  async request<T>(
    endpoint: string,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const method = options.method || "GET";
    const isGetRequest = method === "GET";
    const shouldDedupe =
      options.dedupe !== undefined ? options.dedupe : isGetRequest;

    // Check for pending request
    if (shouldDedupe) {
      const cacheKey = this.getCacheKey(endpoint, options);
      const pendingRequest = this.pendingRequests.get(cacheKey);

      if (pendingRequest) {
        return pendingRequest;
      }
    }

    // Create new request promise
    const requestPromise = this.executeRequest<T>(endpoint, options);

    // Store pending request only if deduplication is enabled
    if (shouldDedupe) {
      const cacheKey = this.getCacheKey(endpoint, options);
      this.pendingRequests.set(cacheKey, requestPromise);

      // Clean up when request completes.
      // Attach .catch so the finally-chain promise does not become an
      // unhandledrejection when the request fails (callers still await
      // requestPromise and handle errors themselves).
      void requestPromise
        .finally(() => {
          this.pendingRequests.delete(cacheKey);
        })
        .catch(() => {});
    }

    return requestPromise;
  }

  /**
   * Execute the actual HTTP request
   * @param retryCount - Internal counter to prevent infinite retry loops
   */
  protected async executeRequest<T>(
    endpoint: string,
    options: RequestOptions = {},
    retryCount: number = 0,
  ): Promise<ApiResponse<T>> {
    // Build URL
    const queryString = buildQueryString(options.params);
    const url = buildUrl(this.baseUrl, endpoint, queryString);

    // Get current token from storage (ensures sync between instances)
    // For retries, prefer instance token which should have been set after refresh
    const currentToken =
      retryCount > 0
        ? this.token ||
          (typeof window !== "undefined"
            ? TokenStorage.get("auth_token")
            : null)
        : typeof window !== "undefined"
          ? TokenStorage.get("auth_token")
          : this.token;

    // Sync instance token
    if (currentToken && currentToken !== this.token) {
      this.token = currentToken;
    }

    // Build headers with current token
    const orgId = getCurrentOrgId();
    const tokenToUse = this.token || currentToken;
    const headers = buildHeaders(options, tokenToUse, orgId, endpoint);

    // Debug logging for retries
    if (retryCount > 0) {
    }

    // Get timeout
    const timeout = options.timeout ?? DEFAULT_TIMEOUT;

    // Clone FormData BEFORE first request (FormData can only be read once)
    // Store clone separately for potential retries (don't modify options.body to preserve original)
    let bodyForRequest: BodyInit | null = options.body || null;
    if (options.body instanceof FormData) {
      // Clone FormData before it's consumed by fetch
      // Store the clone in a way that retry handler can access it
      const clonedFormData = new FormData();
      for (const [key, value] of options.body.entries()) {
        clonedFormData.append(key, value);
      }
      bodyForRequest = clonedFormData;
      // Store cloned FormData in options for retry handler (as a non-enumerable property)
      // This preserves the original FormData reference while providing a clone for retries
      (options as any)._clonedFormData = clonedFormData;
    }

    // Execute request with abort signal management
    let response: Response;
    let signalConfig: AbortSignalConfig | null = null;

    try {
      // Create abort signal
      signalConfig = createAbortSignal(timeout, options.signal || undefined);

      // Extract only valid RequestInit properties (exclude custom RequestOptions properties)
      const {
        dedupe,
        timeout: _timeout,
        params: _params,
        orgId: _orgId,
        ...fetchOptions
      } = options;

      // Debug logging for retries to diagnose 401 issues
      if (retryCount > 0) {
      }

      // Make fetch request with only valid RequestInit properties
      response = await fetch(url, {
        ...fetchOptions,
        body: bodyForRequest, // Use cloned FormData if available
        headers,
        signal: signalConfig.signal,
        credentials: "include",
      });

      // Cleanup signal
      signalConfig.cleanup();
      signalConfig = null;
    } catch (fetchError: unknown) {
      // Cleanup signal
      if (signalConfig) {
        signalConfig.cleanup();
      }

      // Handle abort errors (React Query cancellation or timeout)
      if (isAbortError(fetchError)) {
        // Re-throw abort errors - React Query handles them silently
        throw fetchError;
      }

      // Handle network errors
      const { type, message } = categorizeNetworkError(
        fetchError,
        url,
        timeout,
      );
      const error: ApiError = {
        error: type,
        message,
        statusCode: 0,
      };

      // Log errors in development
      if (typeof window !== "undefined" && (import.meta as any)?.env?.DEV) {
      }

      throw error;
    }

    // Handle 401 Unauthorized (token refresh)
    if (response.status === 401) {
      return this.handle401Response<T>(response, endpoint, options, retryCount);
    }

    // Handle other error status codes
    if (!response.ok) {
      const error = await parseErrorResponse(response);
      throw error;
    }

    // Parse successful response
    try {
      const parsed = await parseResponse<ApiResponse<T>>(response);
      return validateResponse<T>(parsed, response.status);
    } catch (parseError: unknown) {
      const error: ApiError = {
        error: "Invalid Response",
        message:
          parseError instanceof Error
            ? parseError.message
            : "Server returned invalid response",
        statusCode: response.status,
      };
      throw error;
    }
  }

  /**
   * Handle 401 Unauthorized response (attempt token refresh)
   * @param retryCount - Number of times this request has been retried (prevents infinite loops)
   */
  private async handle401Response<T>(
    response: Response,
    endpoint: string,
    options: RequestOptions,
    retryCount: number = 0,
  ): Promise<ApiResponse<T>> {
    const errorMessage = await parse401Error(response);
    const MAX_RETRIES = 1; // Only retry once after token refresh

    // Guard against infinite loop - never retry refresh endpoint
    if (endpoint.includes(AUTH_REFRESH_ENDPOINT)) {
      this.setToken(null);
      this.setRefreshToken(null);
      emitAuthEvent("expired", "Token refresh failed - please login again");
      throw new Error("Token refresh failed - please login again");
    }

    // Prevent infinite retry loops
    if (retryCount >= MAX_RETRIES) {
      this.setToken(null);
      this.setRefreshToken(null);
      emitAuthEvent(
        "expired",
        "Authentication failed after retry. Please login again.",
      );
      throw new Error("Authentication failed after retry. Please login again.");
    }

    // Get current token from storage
    const currentToken =
      typeof window !== "undefined"
        ? TokenStorage.get("auth_token")
        : this.token;

    // Get refresh token to check if refresh is possible
    const currentRefreshToken =
      typeof window !== "undefined"
        ? TokenStorage.get("refresh_token")
        : this.refreshTokenValue;

    // Determine if we should attempt refresh
    const hadToken = currentToken !== null && currentToken !== undefined;
    const hasRefreshToken =
      currentRefreshToken !== null && currentRefreshToken !== undefined;
    const isAuthEndpoint =
      endpoint.includes(AUTH_LOGIN_ENDPOINT) ||
      endpoint.includes(AUTH_REGISTER_ENDPOINT);
    const shouldRefreshToken = hadToken && hasRefreshToken && !isAuthEndpoint;

    if (shouldRefreshToken) {
      try {
        // Attempt token refresh
        await this.refreshTokenSafely();

        // Wait a moment to ensure token is synced to storage
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Get fresh token from storage after refresh
        const refreshedToken =
          typeof window !== "undefined"
            ? TokenStorage.get("auth_token")
            : this.token;

        if (!refreshedToken) {
          throw new Error("Token refresh succeeded but no token found");
        }

        // Sync instance token
        this.token = refreshedToken;

        // Verify token is actually different from before refresh
        const tokenBeforeRefresh = currentToken;
        const tokenChanged = refreshedToken !== tokenBeforeRefresh;

        if (!tokenChanged && tokenBeforeRefresh) {
        }

        // Clear cache for this request
        const cacheKey = this.getCacheKey(endpoint, options);
        this.pendingRequests.delete(cacheKey);

        // Clone body if needed (FormData can only be read once)
        const retryOptions = { ...options };
        if (options.body instanceof FormData) {
          // Use the pre-cloned FormData if available (cloned before first request)
          const clonedFormData = (options as any)._clonedFormData;
          if (clonedFormData instanceof FormData) {
            // Create a fresh clone from the pre-cloned FormData for retry
            const newFormData = new FormData();
            let entryCount = 0;
            for (const [key, value] of clonedFormData.entries()) {
              newFormData.append(key, value);
              entryCount++;
            }
            retryOptions.body = newFormData;
            // Store new clone for potential future retries
            (retryOptions as any)._clonedFormData = newFormData;
          } else {
            try {
              const newFormData = new FormData();
              let entryCount = 0;
              for (const [key, value] of options.body.entries()) {
                newFormData.append(key, value);
                entryCount++;
              }

              retryOptions.body = newFormData;
            } catch (error) {
              throw new Error("Cannot retry: FormData was already consumed");
            }
          }
        }

        // Clear headers to ensure fresh headers are built with new token
        // Services should not set Content-Type for FormData - browser handles it
        // Preserve any custom headers that aren't auth-related
        const preservedHeaders: Record<string, string> = {};
        if (options.headers) {
          const originalHeaders = options.headers as Record<string, string>;
          // Preserve non-auth headers (but not Content-Type for FormData)
          Object.keys(originalHeaders).forEach((key) => {
            const lowerKey = key.toLowerCase();
            const value = originalHeaders[key];
            if (
              value !== undefined &&
              typeof value === "string" &&
              lowerKey !== "authorization" &&
              !(
                retryOptions.body instanceof FormData &&
                lowerKey === "content-type"
              )
            ) {
              preservedHeaders[key] = value;
            }
          });
        }
        retryOptions.headers =
          Object.keys(preservedHeaders).length > 0
            ? preservedHeaders
            : undefined;

        // Force use of refreshed token by ensuring it's in storage and instance
        // Double-check token is in storage before retry
        const tokenInStorage =
          typeof window !== "undefined"
            ? TokenStorage.get("auth_token")
            : this.token;

        if (tokenInStorage !== refreshedToken) {
          // Force sync - ensure token is set in both places
          this.token = refreshedToken;
          if (typeof window !== "undefined") {
            TokenStorage.set("auth_token", refreshedToken);
          }
        }

        // Additional delay to ensure token is fully synced
        await new Promise((resolve) => setTimeout(resolve, 50));

        // Verify token one more time before retry
        const finalTokenCheck =
          typeof window !== "undefined"
            ? TokenStorage.get("auth_token")
            : this.token;

        if (finalTokenCheck !== refreshedToken) {
          throw new Error(
            "Token refresh succeeded but token not properly synced",
          );
        }

        // CRITICAL: Verify the refreshed token has orgId (required for API requests)
        // Decode token payload to check for orgId (safe - just reading, not verifying signature)
        try {
          const tokenParts = refreshedToken.split(".");
          if (tokenParts.length === 3) {
            const payloadPart = tokenParts[1];
            if (!payloadPart) {
              throw new Error("Invalid token format: missing payload");
            }
            const payload = JSON.parse(atob(payloadPart));
            if (
              !payload.orgId &&
              (!payload.orgIds ||
                !Array.isArray(payload.orgIds) ||
                payload.orgIds.length === 0)
            ) {
              throw new Error(
                "Refreshed token is missing orgId. Please login again.",
              );
            }
          }
        } catch {
          // Don't fail retry if we can't decode - let server handle it
        }

        // CRITICAL: Ensure instance token is set before retry
        // This is important because executeRequest checks this.token first for retries
        this.token = refreshedToken;

        // Retry the original request with new token
        const result = await this.executeRequest<T>(
          endpoint,
          retryOptions,
          retryCount + 1,
        );

        return result;
      } catch (refreshError: unknown) {
        // Refresh failed, logout and clear tokens
        this.setToken(null);
        this.setRefreshToken(null);
        const message =
          refreshError instanceof Error
            ? refreshError.message
            : "Your session has expired. Please login again.";
        emitAuthEvent("expired", message);
        throw new Error(message);
      }
    } else {
      // No refresh token available, or this is an auth endpoint
      // If we had a token but no refresh token, emit expired event
      if (hadToken && !hasRefreshToken && !isAuthEndpoint) {
        this.setToken(null);
        this.setRefreshToken(null);
        emitAuthEvent(
          "expired",
          "Your session has expired. Please login again.",
        );
      }
      // Return the actual API error
      throw new Error(errorMessage);
    }
  }

  // ==========================================================================
  // Convenience Methods
  // ==========================================================================

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  /**
   * POST request
   *
   * Thin wrapper - passes body through as-is.
   * Services should handle body serialization (JSON.stringify for objects, FormData as-is, etc.)
   *
   * If options.body is set, it takes precedence (for FormData, etc.)
   * Otherwise, data is serialized: objects → JSON string, FormData/strings → as-is
   */
  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    const requestOptions: RequestOptions = {
      ...options,
      method: "POST",
    };

    // If body is explicitly set in options, use it (takes precedence)
    // This allows services to pass FormData directly via options.body
    if (options?.body !== undefined) {
      requestOptions.body = options.body;
    } else if (data !== undefined && data !== null) {
      // Otherwise, serialize data appropriately
      if (typeof data === "string" || data instanceof FormData) {
        // Pass through strings and FormData as-is
        requestOptions.body = data;
      } else {
        // Stringify objects/arrays for JSON
        requestOptions.body = JSON.stringify(data);
      }
    }

    return this.request<T>(endpoint, requestOptions);
  }

  /**
   * PUT request
   *
   * Thin wrapper - passes body through as-is.
   * Services should handle body serialization.
   */
  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    const body =
      data !== undefined && data !== null
        ? options?.body !== undefined
          ? options.body
          : typeof data === "string" || data instanceof FormData
            ? data
            : JSON.stringify(data)
        : undefined;

    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body,
    });
  }

  /**
   * PATCH request
   *
   * Thin wrapper - passes body through as-is.
   * Services should handle body serialization.
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    const body =
      data !== undefined && data !== null
        ? options?.body !== undefined
          ? options.body
          : typeof data === "string" || data instanceof FormData
            ? data
            : JSON.stringify(data)
        : undefined;

    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(
    endpoint: string,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }

  /**
   * Logout - clear auth state
   */
  logout(): void {
    this.setToken(null);
    this.setRefreshToken(null);
    // Backend should clear httpOnly cookie on logout
    // Frontend clears localStorage token
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

// Create singleton instance - use dynamic URL resolution
// This ensures the URL is evaluated when the instance is created, not at module load
export const apiClient = new ApiClient(getApiBaseUrl());
